import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import CancelRounded from '@mui/icons-material/CancelRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { COLORS, headerCellSx, bodyCellSx } from './storeManagementConstants';
import { StoreManagementTableRow } from './StoreManagementTableRow';

export function StoreManagementStoresTable({
  loadError,
  loading,
  rows,
  page,
  limit,
  totalKitchens,
  onPageChange,
  onLimitChange,
  openMenu,
  onViewStore,
}) {
  return (
    <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
      {loadError && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CancelRounded sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
          <Typography
            sx={{
              color: '#dc2626',
              fontFamily: 'Inter Regular, sans-serif',
              fontSize: 14,
            }}
          >
            {loadError}
          </Typography>
        </Box>
      )}

      <TableContainer
        sx={{
          bgcolor: COLORS.white,
          borderRadius: '14px',
          border: `1px solid ${COLORS.border}`,
          overflowX: 'auto',
          maxWidth: 1070,
          boxShadow: '0 1px 6px rgba(63, 63, 110, 0.06)',
        }}
      >
        <Table
          size="small"
          sx={{
            borderCollapse: 'collapse',
            borderSpacing: 0,
            '& .MuiTableCell-root': {
              boxSizing: 'border-box',
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, minWidth: 200 }}>Kitchen</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 90 }}>Products</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 80 }}>Address</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>Visibility</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 100 }}>Status</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 60, textAlign: 'center' }}>EIN</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 60, textAlign: 'center' }}>Menu</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 70, textAlign: 'center' }}>Device</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 60, textAlign: 'center' }}>Mgr</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 80, textAlign: 'center' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  sx={{ ...bodyCellSx, textAlign: 'center', py: 8, borderBottom: 0 }}
                >
                  <CircularProgress size={28} sx={{ color: COLORS.accentOrange }} />
                  <Typography
                    sx={{
                      mt: 1.5,
                      fontFamily: 'Inter Regular, sans-serif',
                      fontSize: 13,
                      color: COLORS.muted,
                    }}
                  >
                    Loading stores…
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && !loadError && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  sx={{ ...bodyCellSx, textAlign: 'center', py: 8, borderBottom: 0 }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '18px',
                      bgcolor: COLORS.headerBg,
                      border: `1px solid ${COLORS.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <StorefrontOutlinedIcon sx={{ fontSize: 28, color: COLORS.muted }} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter Bold, sans-serif',
                      fontSize: 15,
                      color: COLORS.ink,
                      mb: 0.5,
                    }}
                  >
                    No stores found
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Inter Regular, sans-serif',
                      fontSize: 13,
                      color: COLORS.muted,
                    }}
                  >
                    No kitchens found for this managing branch.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((row, index) => (
                <StoreManagementTableRow
                  key={row.id}
                  row={row}
                  index={index}
                  openMenu={openMenu}
                  onViewStore={onViewStore}
                />
              ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalKitchens}
          page={Math.max(0, page - 1)}
          onPageChange={(_, nextZeroBased) => onPageChange(nextZeroBased + 1)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => {
            const next = Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 20));
            onLimitChange(next);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Rows per page"
          sx={{
            borderTop: `1px solid ${COLORS.rowDivider}`,
            bgcolor: COLORS.headerBg,
            fontFamily: 'Inter Regular, sans-serif',
            color: COLORS.ink,
            '& .MuiTablePagination-toolbar': { minHeight: 52, px: 2 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: 13,
              color: COLORS.muted,
              fontFamily: 'Inter Regular, sans-serif',
            },
            '& .MuiIconButton-root': {
              color: COLORS.ink,
              borderRadius: '8px',
              transition: 'all 0.15s ease',
            },
            '& .MuiIconButton-root:hover': {
              bgcolor: 'rgba(255, 123, 41, 0.10)',
              color: COLORS.accentOrange,
            },
          }}
        />
      </TableContainer>
    </Box>
  );
}
